import React, { useEffect, useState } from 'react';
import ToggleZoomOverlay from 'components/ToggleZoomOverlay';
import PropTypes from 'prop-types';
import Button from 'components/Button';
import classNames from 'classnames';
import useCore from 'hooks/useCore';
import { useTranslation } from 'react-i18next';
import downloadPdf from 'helpers/downloadPdf';
import { useDispatch, useSelector } from 'react-redux';
import selectors from 'selectors';
import DataElements from 'constants/dataElement';
import actions from 'actions';

import './DocumentHeader.scss';

const propTypes = {
  documentViewerKey: PropTypes.number.isRequired,
  docLoaded: PropTypes.bool.isRequired,
  isSyncing: PropTypes.bool.isRequired,
  canSync: PropTypes.bool.isRequired,
};

// Todo Compare: Make stories for this component
const DocumentHeader = ({
  documentViewerKey,
  docLoaded,
  isSyncing,
  canSync,
}) => {
  const { t } = useTranslation();
  const { core } = useCore(documentViewerKey);
  const dispatch = useDispatch();
  const [filename, setFileName] = useState('Untitled');
  const tabManager = useSelector(selectors.getTabManager);
  const activeTab = useSelector(selectors.getActiveTab);
  const isMultiTab = useSelector(selectors.getIsMultiTab);
  const [saveButtonDisabled] = useSelector((state) => [selectors.isElementDisabled(state, DataElements.MULTI_VIEWER_SAVE_DOCUMENT_BUTTON)]);

  useEffect(() => {
    const stopSyncing = () => dispatch(actions.setSyncViewer(null));
    const onLoaded = () => setFileName(core.getDocument(documentViewerKey)?.getFilename());
    const unLoaded = () => setFileName('Untitled');
    core.addEventListener('documentLoaded', onLoaded, undefined, documentViewerKey);
    core.addEventListener('documentUnloaded', unLoaded, undefined, documentViewerKey);
    core.addEventListener('displayModeUpdated', stopSyncing, undefined, documentViewerKey);
    setFileName(core.getDocument()?.getFilename() || 'Untitled');
    return () => {
      core.removeEventListener('documentLoaded', onLoaded, documentViewerKey);
      core.removeEventListener('documentUnloaded', unLoaded, documentViewerKey);
      core.removeEventListener('displayModeUpdated', stopSyncing, documentViewerKey);
    };
  }, [documentViewerKey]);

  const closeDocument = async () => {
    if (isMultiTab && tabManager && (activeTab || activeTab === 0)) {
      if (documentViewerKey !== 1) {
        await tabManager.updateTab(activeTab, {
          isMultiViewer: true,
          clearDocumentForViewerKey: documentViewerKey,
        });
      } else if (documentViewerKey === 1) {
        await tabManager.updateTab(activeTab, {
          isMultiViewer: true,
          clearPrimaryDocument: true,
        });
      }
    }
    core.closeDocument(documentViewerKey);
  };
  const onClickSync = () => dispatch(actions.setSyncViewer(isSyncing ? null : documentViewerKey));
  const onSaveDocument = () => downloadPdf(dispatch, undefined, documentViewerKey);

  return (
    <div
      className={classNames('DocumentHeader', { hidden: !docLoaded })}
      id={`header${documentViewerKey}`}
    >
      <ToggleZoomOverlay documentViewerKey={documentViewerKey} />
      <div className="file-name">{filename}</div>
      <div className="control-buttons">
        {!saveButtonDisabled &&
          <Button img="icon-save" onClick={onSaveDocument} dataElement={DataElements.MULTI_VIEWER_SAVE_DOCUMENT_BUTTON} title={t('multiViewer.save')} />
        }
        { canSync &&
          <Button img="icon-sync" onClick={onClickSync} isActive={isSyncing} title={t(`multiViewer.${isSyncing ? 'stop' : 'start'}Sync`)} />
        }
        <Button img="icon-close" onClick={closeDocument} title={t('multiViewer.closeDocument')} />
      </div>
    </div>
  );
};

DocumentHeader.propTypes = propTypes;

export default DocumentHeader;
